import { findFrame } from '@spin/shared';
import { type ReactNode, useEffect, useState } from 'react';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';

export function Avatar({
  name,
  photoUrl,
  size = 40,
}: {
  name: string;
  photoUrl: string | null;
  size?: number | string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const style = { width: size, height: size, fontSize: typeof size === 'number' ? size * 0.42 : undefined };
  // A photo that cannot load (an expired Telegram link, a removed upload) shows the initial instead.
  if (photoUrl && failed !== photoUrl)
    return (
      <img
        className="avatar"
        src={photoUrl}
        alt=""
        style={style}
        referrerPolicy="no-referrer"
        draggable={false}
        onError={() => setFailed(photoUrl)}
      />
    );
  const initials = Array.from(name.trim()).slice(0, 1).join('').toUpperCase() || '?';
  return (
    <div className="avatar" style={style} aria-hidden>
      {initials}
    </div>
  );
}

/** A name with the frame's side symbols and a golden VIP look. */
export function PlayerName({ name, frame, vip }: { name: string; frame?: string | null; vip?: boolean }) {
  const icon = findFrame(frame)?.nameIcon;
  return (
    <span className={vip ? 'player-name vip' : 'player-name'}>
      {icon && <i className="name-icon">{icon}</i>}
      {name}
      {icon && <i className="name-icon">{icon}</i>}
      {vip && <span className="vip-badge">VIP</span>}
    </span>
  );
}

export function Sheet({ children, onClose }: { children: ReactNode; onClose?: () => void }) {
  const close = onClose ?? useUi.getState().closeSheet;
  useEffect(() => tg.backButton.show(close), [close]);
  return (
    <>
      <div className="backdrop" onClick={close} />
      <div className="sheet" role="dialog" aria-modal>
        <div className="handle" />
        {children}
      </div>
    </>
  );
}

export function Toasts() {
  const toasts = useUi((s) => s.toasts);
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.tone}`} role="status">
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function Spinner() {
  return <div className="spinner" role="progressbar" aria-busy />;
}

/** Shows Telegram's Back button while `active`, calling `onBack` when tapped. */
export function useBackButton(onBack: () => void, active = true): void {
  useEffect(() => (active ? tg.backButton.show(onBack) : undefined), [onBack, active]);
}

export function ScreenHeader({ title, right }: { title: string; right?: ReactNode }) {
  const back = useSession((s) => s.back);
  const depth = useSession((s) => s.stack.length);
  useBackButton(back, depth > 1);
  return (
    <div className="topbar">
      {!tg.inTelegram && depth > 1 && (
        <button className="icon-btn" onClick={back} aria-label="back">
          ←
        </button>
      )}
      <h1 className="title" style={{ fontSize: 20 }}>
        {title}
      </h1>
      <div className="spacer" />
      {right}
    </div>
  );
}
